import { isDrivePath } from '~/utils/drive-route'

const LIVECHAT_TRACKING_URL = 'https://cdn.livechatinc.com/tracking.js'
const LIVECHAT_QB_URL = 'https://cdn.livechatinc.com/qb/qb-11822487-light-160.js'

declare global {
  interface Window {
    __lc?: Record<string, unknown>
    LiveChatWidget?: unknown
  }
}

function setupLiveChatWidgetStub() {
  if (window.LiveChatWidget) return

  window.__lc = window.__lc || {}
  window.__lc.license = 11822487
  window.__lc.integration_name = 'manual_onboarding'
  window.__lc.product_name = 'livechat'

  ;(function (n: Window, _t: Document, c: () => unknown[]) {
    function i(args: unknown[]) {
      return e._h ? e._h.apply(null, args) : e._q.push(args)
    }
    const e: {
      _q: unknown[]
      _h: ((...args: unknown[]) => unknown) | null
      _v: string
      on: (...args: unknown[]) => void
      once: (...args: unknown[]) => void
      off: (...args: unknown[]) => void
      get: (...args: unknown[]) => unknown
      call: (...args: unknown[]) => void
    } = {
      _q: [],
      _h: null,
      _v: '2.0',
      on: function () {
        i(['on', c.call(arguments)])
      },
      once: function () {
        i(['once', c.call(arguments)])
      },
      off: function () {
        i(['off', c.call(arguments)])
      },
      get: function () {
        if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.")
        return i(['get', c.call(arguments)])
      },
      call: function () {
        i(['call', c.call(arguments)])
      },
    }
    n.LiveChatWidget = n.LiveChatWidget || e
  })(window, document, [].slice)
}

function injectHeadScript(src: string) {
  if (document.querySelector(`script[src="${src}"]`)) return
  const script = document.createElement('script')
  script.async = true
  script.type = 'text/javascript'
  script.src = src
  document.head.appendChild(script)
}

function injectBodyScript(src: string) {
  if (document.querySelector(`script[src="${src}"]`)) return
  const script = document.createElement('script')
  script.async = true
  script.src = src
  document.body.appendChild(script)
}

const FOREIGN_LIVECHAT_SELECTOR = 'script[src*="livechat.com/api/"], script[src*="wordpress.livechat.com"]'

// Wait a beat after consent so any LiveChat tag fired by GTM has time to put its
// own script in the DOM — otherwise both installations race for `window.__lc`.
const LOAD_DELAY_MS = 1000

// True when some other LiveChat installation (today: a leftover "LiveChat
// WordPress" tag in GTM-PBDGTKV2) already owns the widget on this page.
// Two installations racing for `window.__lc` is what makes the WordPress script
// throw "`url` is not allowed" on a non-WordPress site, so we stand down instead
// of adding a second widget.
function hasForeignLiveChat() {
  if (document.querySelector(FOREIGN_LIVECHAT_SELECTOR)) return true

  const lc = window.__lc
  return Boolean(lc && lc.license !== 11822487)
}

function loadLiveChat() {
  if (window.LiveChatWidget || hasForeignLiveChat()) return

  setupLiveChatWidgetStub()
  injectHeadScript(LIVECHAT_TRACKING_URL)
  injectBodyScript(LIVECHAT_QB_URL)
}

export default defineNuxtPlugin(() => {
  if (import.meta.server) return

  const consent = useConsentStore()
  const router = useRouter()
  let scheduled = false

  function tryLoadLiveChat() {
    if (isDrivePath(router.currentRoute.value.path)) return
    if (consent.state !== 'accepted' || scheduled) return
    scheduled = true
    window.setTimeout(loadLiveChat, LOAD_DELAY_MS)
  }

  watch(() => consent.state, tryLoadLiveChat, { immediate: true })
})
